import type { NotifyKind } from '@safetyhub/shared';
import { normalizeNotifyChannels } from '@safetyhub/shared';
import type { ApprovalLinePreviewData } from '../lib/reports';

const KEY_PREFIX = 'safetyhub:delegation-default';

/**
 * 마지막 위임에서 쓴 추가 결재자 1명(level 2+).
 *
 * 신원 참조만 담고 이름·연락처는 담지 않는다 — 저장 시점 이름을 되살리면 그 사이 바뀐 정보가
 * 화면에 남는다. 복원 후 표시는 결재라인 섹션이 현재 멤버 목록으로 채우고, 사라진 사람은
 * 그쪽의 신원 유실 표시가 맡는다.
 */
export interface DelegationApprovalLine {
  /**
   * 결재자 신원 — 유일한 축. 구 `approverProfileId` 축은 2026-09-16 E4에서 사라졌다 — 그 키만 가진
   * 옛 저장분(F-J 이전)은 가리킬 사람이 없어 복원 필터(`isRestorableLine`)가 버린다.
   */
  memberId?: string;
  approvalLevel: number;
  label?: string;
  /** 통지 채널 — **정본 축**(NotifyKind[], 2026-08-23 단일화). */
  notifyVia?: NotifyKind[];
  /**
   * @deprecated 구 저장분 읽기 호환({kind}[] — F-C~F-E 사이 번들이 남긴 localStorage 값).
   * 복원 경계(`restorableAdditionalLines`)가 `notifyVia`로 정규화해 흡수하므로, 복원 결과와
   * 신규 저장분에는 이 키가 없다.
   */
  notifyChannels?: Array<{ kind: NotifyKind }>;
}

interface DelegationDefault {
  contactId: string;
  notifyChannels: ('sms' | 'email')[];
  /** 마지막 위임의 추가 결재자(level 2+). 구 저장분에는 없다. */
  additionalLines?: DelegationApprovalLine[];
  /**
   * 저장 시점 현장 기본(고정 결재란)의 서명.
   *
   * 관리자가 현장 기본 결재라인을 바꾸면 내 로컬에 남은 옛 결재라인이 그 변경을 조용히
   * 덮어쓴다. 그래서 복원 전에 이 값을 현재 서명과 대조해, 다르면 **조직 정책이 이긴다**
   * (결재라인만 버리고 담당자·알림 채널 복원은 유지 — 그쪽은 정책과 무관한 개인 선택이다).
   */
  baseSignature?: string;
  savedAt: number;
}

/**
 * 저장 키. 마지막 세그먼트 `COMPOSED`는 유형 축이 있던 시절의 잔재지만, 사용자 브라우저에 이미
 * 저장된 키가 그대로 읽히도록 **키 형식은 바꾸지 않는다**(세그먼트를 지우면 지난 위임 기본값이
 * 전부 유실된다).
 */
function buildKey(userId: string, siteId: string): string {
  return `${KEY_PREFIX}:${userId}:${siteId}:COMPOSED`;
}

/**
 * 현장 기본(고정 결재란)의 안정 서명 — 정책이 바뀌었는지 판정하는 축.
 *
 * `ApprovalLinePreviewData`에는 변경 시각이 없어서 "언제 바뀌었나"로는 비교할 수 없다.
 * 대신 baseRows 자체를 서명으로 만들어 **내용이 달라졌는지**를 본다(서버 확장 불요).
 * 추가 결재자는 사용자 몫이라 서명에 넣지 않는다 — 넣으면 내가 한 편집이 매번 정책 변경으로
 * 오인돼 복원이 늘 무효가 된다.
 */
export function approvalBaseSignature(
  preview: ApprovalLinePreviewData | null | undefined,
): string {
  if (!preview?.hasApprovalLinePolicy) return '';
  return JSON.stringify(
    preview.baseRows.map((r) => [r.level, r.label, r.locked, r.sourceType, r.memberId]),
  );
}

export function getDelegationDefault(
  userId: string,
  siteId: string,
): DelegationDefault | null {
  try {
    const raw = localStorage.getItem(buildKey(userId, siteId));
    return raw ? (JSON.parse(raw) as DelegationDefault) : null;
  } catch {
    return null;
  }
}

export function setDelegationDefault(
  userId: string,
  siteId: string,
  value: {
    contactId: string;
    notifyChannels: ('sms' | 'email')[];
    additionalLines?: DelegationApprovalLine[];
    baseSignature?: string;
  },
): void {
  try {
    localStorage.setItem(
      buildKey(userId, siteId),
      JSON.stringify({ ...value, savedAt: Date.now() }),
    );
  } catch {
    // quota exceeded 등 무시
  }
}

/**
 * localStorage 값 1건이 복원해도 되는 결재자 형태인지 — **저장소는 신뢰 경계 밖**이다.
 *
 * 사용자가 직접 고칠 수 있고 구버전 값도 남는다. 형태를 검사하지 않으면 호출부에서 `.map`
 * TypeError가 나는데, 그 예외는 결재라인 섹션의 초기화 catch에 삼켜져 **복원분뿐 아니라 고정
 * 결재란까지 통째로 비는** 형태로 나타난다(원인 표시 없음).
 */
function isRestorableLine(v: unknown): v is DelegationApprovalLine {
  if (typeof v !== 'object' || v === null) return false;
  const line = v as Record<string, unknown>;
  if (line.memberId !== undefined && typeof line.memberId !== 'string') return false;
  if (line.label !== undefined && typeof line.label !== 'string') return false;
  if (line.notifyVia !== undefined && !Array.isArray(line.notifyVia)) return false;
  // 배열 여부만 본다 — 내용(kind) 검증은 복원 경계의 normalizeNotifyChannels 몫이다(잔존 구 객체도 흡수).
  if (line.notifyChannels !== undefined && !Array.isArray(line.notifyChannels)) return false;
  // 지목할 사람이 없는 행은 복원해도 빈 칸이 될 뿐이다 — 구 프로필 축(`approverProfileId`)만 가진
  // 옛 저장분도 이제 가리킬 대상이 없으므로 같은 이유로 버린다(E4).
  return typeof line.memberId === 'string' && line.memberId !== '';
}

/**
 * 저장분에서 **복원해도 되는** 추가 결재자만 골라낸다.
 *
 * - 정책이 바뀌었으면(서명 불일치) 전부 버린다 — 조직 기본이 이긴다.
 * - 한도가 줄었으면 앞에서부터 남는 만큼만 — 초과분을 들고 있으면 저장 때 거부된다.
 * - level은 고정 결재란 다음부터 다시 매긴다 — 저장 시점의 baseRows 길이가 지금과 다를 수 있다.
 */
export function restorableAdditionalLines(
  // 구조적으로 필요한 것만 받는다 — 전체 DelegationDefault도, 그 부분집합만 들고 있는
  // 호출부(결재라인 섹션)도 그대로 넘길 수 있다.
  stored: { additionalLines?: DelegationApprovalLine[]; baseSignature?: string } | null,
  currentBaseSignature: string,
  baseRowCount: number,
  maxAdditionalLines: number | undefined,
): DelegationApprovalLine[] {
  const raw = stored?.additionalLines;
  // Array.isArray가 먼저다 — 문자열도 `.length`를 가지므로 length 검사만으로는 통과해 버린다.
  if (!Array.isArray(raw) || raw.length === 0) return [];
  if ((stored?.baseSignature ?? '') !== currentBaseSignature) return [];
  const lines = raw.filter(isRestorableLine);
  if (lines.length === 0) return [];
  const limit = maxAdditionalLines ?? lines.length;
  return lines
    .slice(0, Math.max(0, limit))
    .map((line, i) => ({
      ...line,
      approvalLevel: baseRowCount + i + 1,
      // 채널은 정본 축(notifyVia) 하나로 정규화해 복원한다 — 구 저장분의 객체 배열
      // (notifyChannels, 있으면 우선)도 여기서 흡수하므로 하류는 notifyVia만 다룬다.
      notifyVia: normalizeNotifyChannels(line.notifyChannels ?? line.notifyVia),
      notifyChannels: undefined,
    }));
}
